import React, { useRef, useState, useEffect, useMemo } from 'react'
import { HiStatusOnline, HiStatusOffline, HiRefresh } from 'react-icons/hi'
import { createPortal } from 'react-dom'
import { getVotedChoices } from '../../utils/api'

function RealtimeIndicator({ status, activeViewers, choices, deviceId, onReconnect, onRefresh }) {
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const indicatorRef = useRef(null)
  const tooltipTimeoutRef = useRef(null)

  // Calculate user's choices and votes
  const userChoicesCount = useMemo(() => {
    return choices?.filter(c => c.creator_device_id === deviceId).length || 0
  }, [choices, deviceId])

  const userVotesCount = useMemo(() => {
    const votedChoices = getVotedChoices()
    // Count votes only for choices in this poll
    const choiceIds = new Set(choices?.map(c => c.id) || [])
    return Object.keys(votedChoices).filter(choiceId => choiceIds.has(choiceId)).length
  }, [choices])

  const handleMouseEnter = () => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current)
      tooltipTimeoutRef.current = null
    }
    if (indicatorRef.current) {
      const rect = indicatorRef.current.getBoundingClientRect()
      setTooltipPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      })
    }
    setShowTooltip(true)
  }

  const handleMouseLeave = () => {
    tooltipTimeoutRef.current = setTimeout(() => {
      setShowTooltip(false)
    }, 100)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      <div
        ref={indicatorRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.375rem',
          fontSize: '0.875rem',
          color: status === 'connected' ? 'var(--success-color)' :
            status === 'connecting' ? '#f59e0b' :
              status === 'error' ? 'var(--error-color)' : 'var(--text-muted)',
          padding: '0 0.625rem',
          background: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-color)',
          height: '36px',
          minHeight: '36px',
          minWidth: '36px',
          lineHeight: '1.2',
          position: 'relative',
          cursor: 'help',
          transition: 'all var(--transition-base)',
          boxShadow: 'var(--shadow-sm)',
          flexShrink: 0,
          boxSizing: 'border-box'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {status === 'connected' ? (
          <HiStatusOnline style={{
            fontSize: '0.875rem',
            color: 'var(--success-color)',
            filter: 'drop-shadow(0 0 4px rgba(34, 197, 94, 0.4))',
            transition: 'all var(--transition-base)'
          }} />
        ) : status === 'connecting' ? (
          <HiStatusOnline
            className="pulse-icon"
            style={{
              fontSize: '0.875rem',
              color: '#f59e0b',
              filter: 'drop-shadow(0 0 4px rgba(245, 158, 11, 0.4))',
              animation: 'pulse-glow-icon 1.5s ease-in-out infinite'
            }}
          />
        ) : status === 'error' ? (
          <HiStatusOffline style={{
            fontSize: '0.875rem',
            color: 'var(--error-color)',
            transition: 'all var(--transition-base)'
          }} />
        ) : (
          <HiStatusOffline style={{
            fontSize: '0.875rem',
            color: 'var(--text-muted)',
            transition: 'all var(--transition-base)'
          }} />
        )}

      </div>

      {/* Tooltip */}
      {showTooltip && createPortal(
        <div
          onMouseEnter={() => {
            if (tooltipTimeoutRef.current) {
              clearTimeout(tooltipTimeoutRef.current)
              tooltipTimeoutRef.current = null
            }
            setShowTooltip(true)
          }}
          onMouseLeave={() => {
            setShowTooltip(false)
          }}
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateX(-50%)',
            padding: '0.75rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            fontSize: '0.75rem',
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            zIndex: 10000,
            minWidth: '200px',
            animation: 'tooltipFadeIn 0.2s ease-out'
          }}
        >
          <div style={{ fontWeight: '600', marginBottom: '0.5rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.5rem' }}>
            Connection Status
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Status:</span>
              <span style={{ fontWeight: '500', color: status === 'connected' ? 'var(--success-color)' : status === 'error' ? 'var(--error-color)' : 'var(--warning-color)' }}>
                {status === 'connected' ? 'Connected' :
                  status === 'connecting' ? 'Connecting' :
                    status === 'error' ? 'Error' : 'Disconnected'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Active Viewers:</span>
              <span style={{ fontWeight: '500', color: 'var(--accent-primary)' }}>{activeViewers || 0}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Total Choices:</span>
              <span style={{ fontWeight: '500' }}>{userChoicesCount}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Total Votes:</span>
              <span style={{ fontWeight: '500' }}>{userVotesCount}</span>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '0.5rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid var(--border-color)'
          }}>
            {(status === 'error' || status === 'disconnected') && onReconnect && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onReconnect()
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem',
                  padding: '0.375rem 0.5rem',
                  fontSize: '0.7rem',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--accent-primary)'
                  e.currentTarget.style.borderColor = 'var(--accent-primary)'
                  e.currentTarget.style.color = 'white'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-tertiary)'
                  e.currentTarget.style.borderColor = 'var(--border-color)'
                  e.currentTarget.style.color = 'var(--text-primary)'
                }}
              >
                <HiRefresh style={{ fontSize: '0.75rem' }} />
                Reconnect
              </button>
            )}
            {onRefresh && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onRefresh()
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.25rem',
                  padding: '0.375rem 0.5rem',
                  fontSize: '0.7rem',
                  fontWeight: '500',
                  color: 'var(--text-primary)',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--accent-primary)'
                  e.currentTarget.style.borderColor = 'var(--accent-primary)'
                  e.currentTarget.style.color = 'white'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'var(--bg-tertiary)'
                  e.currentTarget.style.borderColor = 'var(--border-color)'
                  e.currentTarget.style.color = 'var(--text-primary)'
                }}
              >
                <HiRefresh style={{ fontSize: '0.75rem' }} />
                Refresh Data
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

export default RealtimeIndicator
